/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Calendar } from 'lucide-react';
import _ from 'lodash';
import { formatDate } from '../../utils/time';
import MemberCell from './MemberCell';
import StatusPill from '../../components/StatusPill';
import API from '../../api/endpoints';
import { sendRequest } from '../../api/shootAPI';

const STATUS_TONE = { approved: 'success', pending: 'warning', rejected: 'error' };
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function ReadOnlyField({ label, value }) {
  return (
    <div>
      <span className="block text-sm font-medium text-ink-900 mb-1.5">{label}</span>
      <div
        className="w-full min-h-10 px-3 py-2 border border-rule rounded-[8px] text-sm text-ink-900"
        style={{ borderStyle: 'solid', backgroundColor: '#f1f2f5' }}
      >
        {value || '—'}
      </div>
    </div>
  );
}
ReadOnlyField.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.string };
ReadOnlyField.defaultProps = { value: '' };

function FaceIdCard({ label, src }) {
  return (
    <div>
      <span className="block text-sm font-medium text-ink-900 mb-1.5">{label}</span>
      <div
        className="rounded-[8px] overflow-hidden border border-surface-2"
        style={{ width: 168, height: 168 }}
      >
        {src ? <img src={src} width="100%" height="100%" alt={label} style={{ objectFit: 'cover' }} /> : null}
      </div>
    </div>
  );
}
FaceIdCard.propTypes = { label: PropTypes.string.isRequired, src: PropTypes.string };
FaceIdCard.defaultProps = { src: '' };

function ViewProfileRequestPopup({
  selectedRequest, isOpen, isUpdating, handleClose, handleApproval,
}) {
  const [faceIdDataPath, setFaceIdDataPath] = useState('');
  const [location, setLocation] = useState(null);
  const [profileUpdateRequests, setProfileUpdateRequests] = useState(null);

  const fetchFaceIdData = async () => {
    const { success, data } = await sendRequest({
      method: 'get',
      url: API.USER.fetchProfileRequest(selectedRequest.member.id),
    });
    if (success) {
      if (data.faceIdData) setFaceIdDataPath(data.faceIdData.image_path);
      if (data.location) setLocation(data.location);
      if (data.profileUpdateRequests) {
        setProfileUpdateRequests(_.keyBy(data.profileUpdateRequests, 'profile_field'));
      }
    }
  };

  useEffect(() => {
    fetchFaceIdData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRequest.profile_field]);

  const isPending = selectedRequest?.status === 'pending';

  return (
    <Modal open={isOpen} onClose={handleClose} className="flex-row align-c jstfy-c" data-testid="view-profile-request-modal">
      <Box style={{
        width: 600,
        maxWidth: 'calc(100% - 32px)',
        maxHeight: '90vh',
        overflowY: 'auto',
        padding: 24,
        background: '#fff',
        borderRadius: 12,
      }}
      >
        {/* Header: status + date */}
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <h5 className="text-lg font-semibold leading-none text-ink-900 mb-0">Profile Update Request</h5>
          <StatusPill tone={STATUS_TONE[selectedRequest?.status] || 'neutral'}>
            {cap(selectedRequest?.status)}
          </StatusPill>
          {selectedRequest?.createdAt && (
            <span className="inline-flex items-center gap-1 text-xs text-ink-500 ml-auto">
              <Calendar size={14} />
              {formatDate(selectedRequest.createdAt)}
            </span>
          )}
        </div>

        {/* Member */}
        <div className="mb-5">
          <MemberCell
            id={selectedRequest?.member?.id}
            profilePicture={selectedRequest?.member?.user?.profilePicture}
            firstName={selectedRequest?.member?.user?.firstName}
            lastName={selectedRequest?.member?.user?.lastName}
            email={selectedRequest?.member?.user?.role || ''}
          />
        </div>

        {/* Field content */}
        {selectedRequest.profile_field === 'address' && (
          <div className="flex flex-col gap-4">
            <ReadOnlyField label="Current Address" value={location?.description} />
            {profileUpdateRequests?.address?.new_value?.formattedAddress && (
              <ReadOnlyField
                label="New Requested Address"
                value={profileUpdateRequests.address.new_value.formattedAddress}
              />
            )}
          </div>
        )}

        {selectedRequest.profile_field === 'face_id' && (
          <div className="flex gap-4 flex-wrap">
            <FaceIdCard label="Current Face ID" src={faceIdDataPath} />
            {profileUpdateRequests?.face_id?.new_value && (
              <FaceIdCard label="Requested Face ID" src={profileUpdateRequests.face_id.new_value.image_path} />
            )}
          </div>
        )}

        {/* Footer */}
        {isPending && (
          <div className="flex justify-start items-center gap-2 mt-6">
            <button
              type="button"
              disabled={isUpdating}
              onClick={() => handleApproval('approved')}
              data-testid="approve"
              className="inline-flex items-center justify-center px-4 py-2 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {isUpdating ? 'Approving…' : 'Approve'}
            </button>
            <button
              type="button"
              disabled={isUpdating}
              onClick={() => handleApproval('rejected')}
              data-testid="reject"
              className="inline-flex items-center justify-center px-4 py-2 bg-[#CC4525] text-white rounded-[8px] text-sm font-semibold hover:bg-[#a8391d] disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {isUpdating ? 'Rejecting…' : 'Reject'}
            </button>
          </div>
        )}
      </Box>
    </Modal>
  );
}

ViewProfileRequestPopup.propTypes = {
  selectedRequest: PropTypes.shape({
    id: PropTypes.number,
    status: PropTypes.string,
    profile_field: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    new_value: PropTypes.object,
    createdAt: PropTypes.string,
    member: PropTypes.shape({
      id: PropTypes.number,
      user: PropTypes.shape({
        profilePicture: PropTypes.string,
        fullName: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        role: PropTypes.string,
      }),
    }),
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  isUpdating: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleApproval: PropTypes.func.isRequired,
};

export default ViewProfileRequestPopup;
